import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { cmsAPI, BACKEND_URL } from './api';

const SITE_NAME = 'DrPortals';
const DEFAULT_IMAGE = '/brand/og-image.png';

export const assetUrl = (u) => (u && u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u);
export const absUrl = (u) => {
  if (!u) return '';
  const a = assetUrl(u);
  return /^https?:\/\//.test(a) ? a : `${window.location.origin}${a.startsWith('/') ? '' : '/'}${a}`;
};

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

const parseSchema = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

let staticRemoved = false;

export const Seo = ({ title, description, path, image, type = 'website', jsonLd = [], override = {}, page = {} }) => {
  const location = useLocation();
  useEffect(() => {
    if (staticRemoved) return;
    document.querySelectorAll('[data-static-seo]').forEach((el) => el.remove());
    staticRemoved = true;
  }, []);

  const src = { ...page, ...Object.fromEntries(Object.entries(override || {}).filter(([, v]) => v !== '' && v !== null)) };
  const metaTitle = pick(src.meta_title, title, SITE_NAME);
  const metaDesc = pick(src.meta_description, description, '');
  const canonical = pick(src.canonical_url, `${window.location.origin}${path || location.pathname}`);
  const index = src.robots_index !== false;
  const follow = src.robots_follow !== false;
  const ogImage = absUrl(pick(src.og_image, image, DEFAULT_IMAGE));
  const schemas = [...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean), ...parseSchema(src.schema_markup)];

  return (
    <Helmet title={String(metaTitle)} script={schemas.map((sc) => ({ type: 'application/ld+json', innerHTML: JSON.stringify(sc) }))}>
      {metaDesc && <meta name="description" content={metaDesc} />}
      <meta name="robots" content={`${index ? 'index' : 'noindex'},${follow ? 'follow' : 'nofollow'}`} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pick(src.og_title, metaTitle)} />
      <meta property="og:description" content={pick(src.og_description, metaDesc, '')} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pick(src.og_title, metaTitle)} />
      <meta name="twitter:description" content={pick(src.og_description, metaDesc, '')} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export const usePageSeo = (path) => {
  const location = useLocation();
  const target = path || location.pathname;
  const [seo, setSeo] = useState({});
  useEffect(() => {
    let active = true;
    cmsAPI.getPageSeo(target).then((r) => active && setSeo(r.data || {})).catch(() => {});
    return () => { active = false; };
  }, [target]);
  return seo;
};

export const tryRedirect = async (path, navigate) => {
  try {
    const { data } = await cmsAPI.resolveRedirect(path);
    navigate(data.to_path, { replace: true });
    return true;
  } catch {
    return false;
  }
};

export const slugify = (text) =>
  (text || '').toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/[\s-]+/g, '-').slice(0, 90).replace(/^-|-$/g, '');
